/** Navigation bar component. */

import { useNavigationStore } from '@/stores/navigation';
import { hideAllViews, showView, setActiveNavLink } from '@/utils/viewNavigation';
import { showToast } from '@/utils/helpers';

export interface NavigationHandlers {
  handleTickerClick: (ticker: string) => void;
  handleInfluencerClick: (authorName: string) => void;
  refreshDashboard: () => Promise<void>;
}

let handlers: NavigationHandlers | null = null;

export function setNavigationHandlers(newHandlers: NavigationHandlers): void {
  handlers = newHandlers;
}

export function getNavigationHandlers(): NavigationHandlers | null {
  return handlers;
}

export function initNavigation(): void {
  const { currentView } = useNavigationStore.getState();

  // Set up navigation click handlers
  document.querySelectorAll('nav').forEach((nav) => {
    nav.addEventListener('click', handleNavClick);
  });

  // Initialize with dashboard view
  if (currentView === 'dashboard' && handlers) {
    handlers.refreshDashboard();
  }
}

async function handleNavClick(event: Event): Promise<void> {
  const target = (event.target as HTMLElement).closest('button.nav-link, button.nav-link-mobile');
  if (!target) return;

  const view = target.getAttribute('data-view');
  if (!view) return;

  const { currentView, setCurrentView } = useNavigationStore.getState();
  if (view === currentView) return;

  // Hide all views
  hideAllViews();

  // Update active nav link
  setActiveNavLink(view);

  // Show loader
  const mainLoader = document.getElementById('main-loader');
  if (mainLoader) {
    mainLoader.classList.remove('hidden');
    mainLoader.style.display = '';
  }

  // Update current view
  setCurrentView(view);

  // Load view data
  await loadViewData(view);

  // Hide loader and show view
  if (mainLoader) {
    mainLoader.classList.add('hidden');
    mainLoader.style.display = 'none';
  }

  showView(view);
}

async function loadViewData(view: string): Promise<void> {
  try {
    switch (view) {
      case 'dashboard':
        if (handlers) {
          await handlers.refreshDashboard();
        }
        break;
      case 'live_feed':
        await import('../views/LiveFeedView').then(module => {
          return module.renderLiveFeed();
        });
        break;
      case 'top_influencers':
        await import('../views/InfluencersView').then(module => {
          return module.renderInfluencers();
        });
        break;
      case 'due_diligence':
        await import('../views/DueDiligenceView').then(module => {
          return module.renderDueDiligence();
        });
        break;
      case 'news':
        await import('../views/NewsView').then(module => {
          return module.renderNews();
        });
        break;
      case 'watchlist':
        await import('../views/WatchlistView').then(module => {
          return module.renderWatchlist();
        });
        break;
      case 'documentation':
        await import('../views/DocumentationView').then(module => {
          return module.renderDocumentation();
        });
        break;
      default:
        break;
    }
  } catch (error) {
    console.error(`Error loading view data for ${view}:`, error);
    showToast(`Failed to load ${view} data. Please retry.`, true, { dedupeKey: `nav-load-${view}` });
  }
}
